import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Permissions } from '../../constants/permissions';
import { langHelper } from '../../helpers-and-utilities/language-helper';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { Menu, NavService } from '../../services/nav.service';

@Component({
  selector: 'app-sidemenu',
  templateUrl: './sidemenu.component.html',
  styleUrls: ['./sidemenu.component.scss']
})
export class SidemenuComponent implements OnInit, AfterViewInit, OnDestroy {

  public menuItems: Menu[] = [];
  public userRole: string | undefined = this.authService.getUserRole();
  public url: any;
  navMenu_selected: number = 0;

  private readonly destroy$ = new Subject<void>();

  @ViewChild('createPool', { read: TemplateRef }) createPool: TemplateRef<any> | undefined;

  constructor(
    private router: Router,
    private navServices: NavService,
    public elRef: ElementRef,
    private modalService: NgbModal,
    public langHelper: langHelper,
    public authService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initializeMenuItems();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initializeMenuItems());

    this.checkActiveRoute(this.router.url);
  }

  ngAfterViewInit() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.initializeMenuItems();
        this.checkActiveRoute(event.urlAfterRedirects);
        document.body.classList.remove('main-sidebar-show');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeMenuItems() {
    const allItems: Menu[] = [
      { headTitle: this.translate.instant('General') },
      {
        path: '/dashboard',
        title: this.translate.instant("Dashboard"),
        type: 'link',
        icon: 'ti-home',
        active: false,
        permission: Permissions.DashboardView,
      },
      {
        path: '/ads', title: 'Ads', type: 'link', icon: 'fe fe-layout', active: false,
        permission: Permissions.AdsView,
      },
      { headTitle: 'Providers' },
      {
        path: '/providers', title: 'Providers', type: 'link', icon: 'mdi mdi-domain', active: false,
        permission: Permissions.ProvidersView,
      },
      { headTitle: 'Candidates' },
      {
        path: '/candidates', title: 'Candidates', type: 'link', icon: 'fe fe-users', active: false,
        permission: Permissions.CandidatesView,
      },
      {
        path: '/cvs', title: 'CVs', type: 'link', icon: 'fe fe-file', active: false,
        permission: Permissions.CvsView,
      },
      {
        path: '/interviews', title: 'Mock Interviews', type: 'link', icon: 'fe fe-video', active: false,
        permission: Permissions.InterviewsView,
      },
      {
        path: '/workshops', title: 'LinkedIn Workshops', type: 'link', icon: 'fe fe-book-open', active: false,
        permission: Permissions.WorkshopsView,
      },
      {
        path: '/process-orders', title: 'Process Orders', type: 'link', icon: 'fe fe-clipboard', active: false,
        permission: Permissions.PackagesProcessView,
      },
      { headTitle: 'Opportunities' },
      {
        path: '/opportunities', title: 'Opportunities', type: 'link', icon: 'mdi mdi-lightbulb-outline', active: false,
        permission: Permissions.OpportunitiesView,
      },
      {
        path: '/collection-sources', title: 'Collection sources', type: 'link', icon: 'mdi mdi-web', active: false,
        permission: Permissions.CollectionSourcesManage,
      },
      { headTitle: 'System' },
      {
        path: '/moderators', title: 'Moderators', type: 'link', icon: 'mdi mdi-account-key', active: false,
        permission: Permissions.ModeratorsView,
      },
      {
        path: '/requests', title: 'Contact Requests', type: 'link', icon: 'mdi mdi-email-outline', active: false,
        permission: Permissions.RequestsView,
      },
      {
        path: '/company-interests', title: 'Company Interests', type: 'link', icon: 'mdi mdi-briefcase', active: false,
        permission: Permissions.RequestsView,
      },
      {
        path: '/payments', title: 'Payments', type: 'link', icon: 'mdi mdi-cash-multiple', active: false,
        permission: Permissions.PaymentsView,
      },
      {
        path: '/system/configuration', title: 'System Configuration', type: 'link', icon: 'mdi mdi-settings', active: false,
        permission: Permissions.SystemConfigView,
      },
      {
        path: '/system/coupons', title: 'Coupons', type: 'link', icon: 'mdi mdi-ticket-percent', active: false,
        permission: Permissions.SystemCouponsView,
      },
      {
        path: '/system/package-catalog', title: 'Package catalog', type: 'link', icon: 'mdi mdi-package-variant', active: false,
        permission: Permissions.SystemPackagesView,
      },
      {
        path: '/system/subscription-plans', title: 'Subscription plans', type: 'link', icon: 'mdi mdi-sync', active: false,
        permission: Permissions.SystemPlansView,
      },
      {
        path: '/system/notification-email-settings', title: 'Notification Email Settings', type: 'link', icon: 'mdi mdi-email-outline', active: false,
        permission: Permissions.SystemNotificationsView,
      },
    ];

    this.menuItems = this.filterMenuByPermission(allItems);
    this.cdr.detectChanges();
  }

  canShowMenuItem(item: Menu): boolean {
    if (item.headTitle) {
      return true;
    }
    if (!item.permission) {
      return false;
    }
    return this.authService.hasPermission(item.permission);
  }

  private filterMenuByPermission(items: Menu[]): Menu[] {
    const filtered: Menu[] = [];
    let index = 0;

    while (index < items.length) {
      const item = items[index];

      if (item.headTitle) {
        const sectionHeader = item;
        const sectionItems: Menu[] = [];
        index++;

        while (index < items.length && !items[index].headTitle) {
          sectionItems.push(items[index]);
          index++;
        }

        const visibleSectionItems = sectionItems
          .map((sectionItem) => this.filterMenuItem(sectionItem))
          .filter((sectionItem): sectionItem is Menu => sectionItem !== null);

        if (visibleSectionItems.length > 0) {
          filtered.push(sectionHeader, ...visibleSectionItems);
        }

        continue;
      }

      const visibleItem = this.filterMenuItem(item);
      if (visibleItem) {
        filtered.push(visibleItem);
      }
      index++;
    }

    return filtered;
  }

  private filterMenuItem(item: Menu): Menu | null {
    if (item.children?.length) {
      const children = item.children
        .map((child) => this.filterMenuItem(child))
        .filter((child): child is Menu => child !== null);

      if (children.length === 0) {
        return null;
      }

      return { ...item, children };
    }

    if (!item.permission || !this.authService.hasPermission(item.permission)) {
      return null;
    }

    return item;
  }

  // Check active route based on URL
  private checkActiveRoute(url: string) {
    if (!this.menuItems?.length) {
      return;
    }

    this.menuItems.forEach(item => {
      item.active = false;

      if (item.path && url.startsWith(item.path)) {
        item.active = true;
      }

      if (item.children) {
        item.children.forEach(child => {
          if (child.path && url.startsWith(child.path)) {
            item.active = true;
            child.active = true;
          }

          if (child.children) {
            child.children.forEach(subChild => {
              if (subChild.path && url.startsWith(subChild.path)) {
                item.active = true;
                child.active = true;
                subChild.active = true;
              }
            });
          }
        });
      }
    });
  }

  //Active NavBar State
  setNavActive(item: any) {
    this.menuItems.filter(menuItem => {
      if (menuItem !== item) {
        menuItem.active = false;
        document.body.classList.remove('main-sidebar-show');
      }
      if (menuItem.children && menuItem.children.includes(item)) {
        menuItem.active = true;
      }
      if (menuItem.children) {
        menuItem.children.filter(submenuItems => {
          if (submenuItems.children && submenuItems.children.includes(item)) {
            menuItem.active = true;
            submenuItems.active = true;
          }
        });
      }
    });
  }

  // Click Toggle menu
  toggleNavActive(item: any) {
    if (!item.active) {
      this.menuItems.forEach((a: any) => {
        if (this.menuItems.includes(item)) {
          a.active = false;
        }
        if (!a.children) { return false; }
        a.children.forEach((b: any) => {
          if (a.children.includes(item)) {
            b.active = false;
          }
        });
        return;
      });
    }
    item.active = !item.active;
  }

  //Modal
  displayModal(item: any) {
    if (item.action == 'createPool') {
      this.modalService.open(this.createPool, { size: 'lg' });
    }
  }
}
